import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	InputAdornment,
	MenuItem,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { Email } from "@mui/icons-material";
import moment from "moment";
import { Client } from "../../../../client_api/model/client";
import { useProfile } from "../../../context/ProfileContext";
import { AutoCompleteClient, AutoCompletePermission, ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../shared/forms";
import { VMaskTextField } from "../../../shared/forms/VMaskTextField";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { clientTypeService, IListagemClientType } from "../../../shared/services/api/clientType/clientTypeService";
import { PermissionServices } from "../../../shared/services/api/permissions/PermissionService";
import { ResetPasswordService } from "../../../shared/services/api/reset-password/ResetPasswordService";
import { IDetalheUsuario, userService } from "../../../shared/services/api/user/userService";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { IPermissionInterface } from "../interface/PermissionInterface";
import { ToolbarUserDetail } from "./ToolbarUserDetail";

const ADMIN_CLIENT_TYPE_KEY = "admin";

interface IFormData {
	name: string;
	email: string;
	phone: string;
	password?: string;
	permissionId: string;
	createdFrom: string;
	status: string;
}

const getValidationSchema = (isNew: boolean): yup.SchemaOf<IFormData> =>
	yup.object().shape({
		name: yup.string().required("Informe o nome"),
		email: yup.string().required("Informe o e-mail").email("E-mail inválido"),
		phone: yup.string().required("Informe o telefone"),
		password: isNew ? yup.string().required("Informe a senha") : yup.string().notRequired(),
		permissionId: yup.string().required("Selecione o perfil"),
		createdFrom: yup.string().default("web_app"),
		status: yup.string().default("active").required("Selecione o status"),
	});

export const DetalheUsuariosPresentation: React.FC = () => {
	const { id = "novo" } = useParams<"id">();
	const navigate = useNavigate();
	const { formRef, save, saveAndClose, saveAndNew, issaveAndClose } = useVForm();
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const { permissionLevel, permissionId, isSuperAdmin, isPermissionInitialized } = useProfile();
	const isNew = id === "novo";
	const canCreate = useMemo(() => checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Add), []);
	const canUpdate = useMemo(() => checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Update), []);
	const canResetPassword = useMemo(() => checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.ResetPassword), []);
	const canSave = isNew ? canCreate : canUpdate;
	const isReadOnly = !canSave;

	const [isLoading, setIsLoading] = useState(false);
	const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
	const [isResetSending, setIsResetSending] = useState(false);
	const [nome, setNome] = useState("");
	const [nomeClient, setNomeClient] = useState("");
	const [clientId, setClientId] = useState("");
	const [functionOptions, setFunctionOptions] = useState<IPermissionInterface[]>([]);
	const [clientTypes, setClientTypes] = useState<IListagemClientType[]>([]);
	const [selectedClientTypeId, setSelectedClientTypeId] = useState<string>("");
	const [userPermissionId, setUserPermissionId] = useState<string>("");
	const toastHandler = useToast();
	const [user, setUser] = useState<IDetalheUsuario | null>(null);

	useEffect(() => {
		if (!isNew) {
			setIsLoading(true);

			userService
				.getByID(id)
				.then((result) => {
					setIsLoading(false);
					if (result instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={"Alerta"} content={result.message} />,
						});
						navigate("/usuarios");
						return;
					}

					setUser(result);
					setNome(result.name);

					const cli = result?.client as Client;
					const nameClient = cli?.name || "";
					const idClient = cli?.id || "";
					setNomeClient(nameClient);
					setClientId(idClient);
					// O cliente já vem embutido no usuário — dá para restringir os perfis sem uma chamada extra.
					setSelectedClientTypeId(String((cli?.clientType as any)?.id ?? (cli as any)?.clientTypeId ?? ""));

					const permissionIdFromApi =
						(result as any).permissionId ?? (result as any).permission_id ?? (result as any).permission?.id ?? "";

					setUserPermissionId(String(permissionIdFromApi));

					formRef.current?.setData({
						...result,
						clientId: idClient,
						permissionId: permissionIdFromApi,
						status: result.status ?? "active",
						createdFrom: "web_app",
						password: "",
					} as any);
				})
				.catch((err: any) => {
					setIsLoading(false);
					const { titleMessage, contentMessage } = returnMessageError(err);
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		} else {
			setUser(null);
			setNome("");
			setNomeClient("");
			setClientId("");
			setSelectedClientTypeId("");
			setUserPermissionId("");
			formRef.current?.setData({
				status: "active",
				name: "",
				email: "",
				phone: "",
				password: "",
				permissionId: "",
				createdFrom: "web_app",
				clientId: "",
			} as any);
		}
	}, [id, isNew]);

	// O clientTypeId gravado no perfil ora é o slug ("admin", "estate"), ora o id do registro — o mesmo
	// "ou" que a tela de Perfis já trata ao carregar. Normalizamos os dois para o slug.
	// Mesmo critério que a tela de Perfis usa em `isAdminClientType`.
	const clientTypeKeyByRawId = useMemo(() => {
		return clientTypes.reduce<Record<string, string>>((acc, clientType) => {
			const key = String(clientType.clientTypeId ?? "").toLowerCase();
			if (!key) return acc;

			acc[key] = key;
			acc[String(clientType.id).toLowerCase()] = key;
			return acc;
		}, {});
	}, [clientTypes]);

	const levelByClientTypeKey = useMemo(() => {
		return clientTypes.reduce<Record<string, number>>((acc, clientType) => {
			const key = String(clientType.clientTypeId ?? "").toLowerCase();
			if (key) acc[key] = Number(clientType.level);
			return acc;
		}, {});
	}, [clientTypes]);

	const resolveClientTypeKey = useMemo(() => {
		return (rawClientTypeId: unknown): string => clientTypeKeyByRawId[String(rawClientTypeId ?? "").toLowerCase()] ?? "";
	}, [clientTypeKeyByRawId]);

	// Perfis criados pela tela de Perfis foram gravados com level 0 (o front enviava 0 chumbado).
	// Nesses casos o nível real é o do clientType do perfil — sem essa normalização eles somem da lista.
	const resolvePermissionLevel = useMemo(() => {
		return (opt: IPermissionInterface): number => {
			const ownLevel = Number(opt.level);
			if (Number.isFinite(ownLevel) && ownLevel > 0) return ownLevel;

			const fallback = levelByClientTypeKey[resolveClientTypeKey(opt.clientTypeId)];
			return Number.isFinite(Number(fallback)) ? Number(fallback) : ownLevel;
		};
	}, [levelByClientTypeKey, resolveClientTypeKey]);

	const permissionOptions = useMemo(() => {
		const currentLevel = permissionLevel;
		const selectedClientTypeKey = resolveClientTypeKey(selectedClientTypeId);

		return (
			functionOptions
				?.filter((opt) => {
					// O perfil já atribuído ao usuário nunca some da lista — senão o campo abriria vazio na edição.
					// (Só exibição: quem não é superusuário continua sem poder trocar para outro perfil admin.)
					if (userPermissionId && String(opt.id) === userPermissionId) return true;

					// Somente superusuário define superusuário: perfil de clientType "admin" fica fora da
					// lista para todo mundo que não seja isSuperAdmin. Não depende de level 0 — esse nunca
					// foi um nível real, era o valor corrompido gravado na criação de perfis.
					if (!isSuperAdmin && resolveClientTypeKey(opt.clientTypeId) === ADMIN_CLIENT_TYPE_KEY) return false;

					// Enquanto não sabemos o level do usuário logado (contexto carregando), mantém as opções
					if (currentLevel !== null && currentLevel !== undefined) {
						// Regra: não permitir atribuir perfis com mais privilégio (nível menor) que o próprio
						if (resolvePermissionLevel(opt) < currentLevel) return false;
					}

					// Perfil é por tipo de cliente: usuário de imobiliária não deve receber perfil de corretora,
					// nem de administrador. Só filtra quando dá para resolver os dois lados — sem o cliente
					// escolhido (ou com clientType desconhecido) a lista segue completa, como era antes.
					const optClientTypeKey = resolveClientTypeKey(opt.clientTypeId);
					if (!selectedClientTypeKey || !optClientTypeKey) return true;

					return optClientTypeKey === selectedClientTypeKey;
				})
				.map((opt) => ({ id: opt.id, label: opt.description })) ?? []
		);
	}, [functionOptions, permissionLevel, resolvePermissionLevel, resolveClientTypeKey, selectedClientTypeId, userPermissionId, isSuperAdmin]);

	// Só busca depois que o ProfileContext resolveu: com deps [] o efeito rodava com permissionLevel
	// ainda null (F5 direto na URL) e pedia /permission/level/99, trazendo uma lista diferente da
	// que aparece quando se chega na tela navegando — e sem nunca refazer a busca.
	useEffect(() => {
		if (!isPermissionInitialized) return;

		const level = Number(permissionLevel);
		const requestedLevel = permissionLevel !== null && permissionLevel !== undefined && Number.isFinite(level) ? level : 99;

		PermissionServices.getUnlimited(requestedLevel)
			.then((result: any) => {
				const data = result.data as unknown as IPermissionInterface[];
				setFunctionOptions(data);
			})
			.catch((err: any) => {
				const { titleMessage, contentMessage } = returnMessageError(err);

				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			});
	}, [isPermissionInitialized, permissionLevel]);

	// Tipos de cliente: usados para normalizar o nível dos perfis e para restringir os perfis ao cliente escolhido.
	useEffect(() => {
		let isMounted = true;

		clientTypeService
			.getAll(1, "", 100)
			.then((response) => {
				if (!isMounted) return;

				setClientTypes(response.data ?? []);
			})
			.catch(() => {
				// Falha aqui só desabilita as normalizações por clientType — a tela segue funcionando.
			});

		return () => {
			isMounted = false;
		};
	}, []);

	const handleSave = (dados: IFormData) => {
		if (!canSave) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Permissão"} content={"Você não tem permissão para salvar alterações."} />,
			});
			return;
		}

		const schema = getValidationSchema(isNew);

		schema
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados) => {
				// Somente superusuário define superusuário. A lista de perfis já esconde os perfis de
				// clientType "admin" de quem não é superusuário; o save revalida para não depender da UI.
				// Vale para cadastro novo e para edição — na edição só bloqueia se o perfil está mudando,
				// senão um não-superusuário não conseguiria salvar nem o telefone de um admin existente.
				const permissionToAssign = functionOptions.find((p) => p.id === dadosvalidados.permissionId);
				const isAdminProfile =
					!!permissionToAssign && resolveClientTypeKey(permissionToAssign.clientTypeId) === ADMIN_CLIENT_TYPE_KEY;
				const isChangingProfile = isNew || String(dadosvalidados.permissionId) !== String(userPermissionId);

				if (isAdminProfile && isChangingProfile && !isSuperAdmin) {
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: (
							<ToastContent title={"Permissão"} content={"Somente um superusuário pode atribuir um perfil de superusuário."} />
						),
					});
					return;
				}

				setIsLoading(true);
				if (isNew) {
					userService.create(dadosvalidados as any).then((result: any) => {
						setIsLoading(false);
						if (result instanceof Error) {
							const titleMessage = "Alerta!!";
							const contentMessage = "Erro: " + result?.message;

							toastHandler.present({
								type: "error",
								position: "top-right",
								messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
							});
						} else {
							toastHandler.present({
								type: "success",
								position: "top-right",
								messageOrContent: <ToastContent title={"Sucesso"} content={"Usuário criado com sucesso!"} />,
							});

							if (issaveAndClose()) {
								navigate(`/usuarios/`);
							} else {
								navigate(`/usuarios/detalhe/${result}`);
							}
						}
					});
				} else {
					const selectedPermission = functionOptions.find((p) => p.id === dadosvalidados.permissionId);

					// `role` vira o claim `user_role` do JWT e é o que o backend usa para autorizar. O
					// fallback antigo (`?? "estate_user"`) rebaixava o usuário em silêncio sempre que o perfil
					// dele não estivesse em `functionOptions` — o que acontece quando o perfil fica fora do
					// recorte de `/permission/level/{level}` (caso dos perfis com `level: 0`). Salvar o
					// cadastro tirava o acesso da pessoa no backend sem mudar nada na tela.
					// Se o perfil não mudou, reaproveita o role que já está gravado; sem resolução confiável,
					// o campo é omitido do PATCH e o backend mantém o valor atual.
					const unchangedProfile = String(dadosvalidados.permissionId) === String(userPermissionId);
					const resolvedRole = selectedPermission?.clientTypeId ?? (unchangedProfile ? user?.role ?? user?.permission?.clientTypeId : undefined);

					if (!resolvedRole) {
						console.warn(
							`[usuarios] role não resolvido para permissionId=${dadosvalidados.permissionId}; PATCH enviado sem 'role' para não rebaixar o usuário.`
						);
					}

					delete (dadosvalidados as any).password; // não atualiza senha por aqui
					const updatePayload: any = { id: id, ...dadosvalidados };
					if (resolvedRole) updatePayload.role = resolvedRole;

					userService.updateById(id, updatePayload).then((result: any) => {
						setIsLoading(false);

						if (result instanceof Error) {
							const titleMessage = "Alerta!!";
							const contentMessage = "Erro: " + result?.message;

							toastHandler.present({
								type: "error",
								position: "top-right",
								messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
							});
						} else {
							toastHandler.present({
								type: "success",
								position: "top-right",
								messageOrContent: <ToastContent title={"Sucesso"} content={"Usuário atualizado com sucesso."} />,
							});

							if (issaveAndClose()) {
								navigate(`/usuarios/`);
							}
						}
					});
				}
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			});
	};

	const handleOpenResetDialog = () => {
		if (isNew) return;
		setIsResetDialogOpen(true);
	};

	const handleConfirmResetPassword = async () => {
		if (!canResetPassword) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Permissão"} content={"Você não tem permissão para resetar senha."} />,
			});
			return;
		}

		const emailToReset = user?.email;
		if (!emailToReset) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Alerta"} content={"Não foi possível identificar o e-mail do usuário."} />,
			});
			return;
		}

		try {
			setIsResetSending(true);
			await ResetPasswordService.save({ email: emailToReset });
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: (
					<ToastContent
						title={"Email Enviado"}
						content={
							"Foi enviado um link com codigo de recuperação para o email cadastrado, verifique sua caixa de entrada e ou lixo/spam"
						}
					/>
				),
			});
			setIsResetDialogOpen(false);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setIsResetSending(false);
		}
	};

	return (
		<LayoutBaseDePagina
			titulo="Usuários"
			subtitulo={isNew ? "Cadastro de novo usuário" : `Edição de usuário • ${nome}`}
			barraDeFerramentas={
				<ToolbarUserDetail
					isLoading={isLoading}
					isNew={isNew}
					canSave={canSave}
					canCreate={canCreate}
					canResetPassword={canResetPassword}
					onSave={save}
					onSaveAndClose={saveAndClose}
					onResetPassword={handleOpenResetDialog}
					onBack={() => navigate("/usuarios")}
					onNew={() => navigate("/usuarios/detalhe/novo")}
				/>
			}
		>
			<Dialog open={isResetDialogOpen} onClose={() => (isResetSending ? null : setIsResetDialogOpen(false))} maxWidth="xs" fullWidth>
				<DialogTitle>Reenviar reset de senha</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						Deseja reenviar o link de reset de senha para o e-mail do usuário?
					</Typography>
					<Typography variant="subtitle2" sx={{ mt: 2 }}>
						{user?.email ?? "--"}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsResetDialogOpen(false)} disabled={isResetSending}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={handleConfirmResetPassword} disabled={isResetSending}>
						{isResetSending ? "Enviando..." : "Confirmar"}
					</Button>
				</DialogActions>
			</Dialog>
			<VForm
				id="formIdSave"
				placeholder=""
				onPointerEnterCapture={() => undefined}
				onPointerLeaveCapture={() => undefined}
				ref={formRef}
				onSubmit={handleSave}
			>
				<Box
					sx={{
						width: "100%",
						maxWidth: "1300px",
						ml: 1,
						mr: 1,
						pb: 2,
					}}
				>
					<LoadingComp isopen={isLoading} />

					{isReadOnly && (
						<Box mt={1}>
							<Alert severity="info">Você não tem permissão para salvar alterações nesta tela.</Alert>
						</Box>
					)}

					<Stack spacing={2} mt={1}>
						<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" padding={2} borderRadius={2}>
							<Box>
								<Typography variant="h6" color="primary">
									Dados do usuário
								</Typography>
							</Box>

							{isNew && (
								<Box mt={2}>
									<Alert
										severity="info"
										variant="filled"
										sx={{
											backgroundColor: alpha(theme.palette.info.main, 0.12),
											color: theme.palette.text.primary,
											"& .MuiAlert-icon": { color: theme.palette.info.main },
										}}
									>
										<AlertTitle>Informações do cadastro</AlertTitle>
										- O e-mail não poderá ser alterado após o cadastro.
										<br />- A senha é obrigatória para novo usuário.
									</Alert>
								</Box>
							)}

							<Grid container padding={2} spacing={2}>
								<Grid item xs={12} md={6}>
									<AutoCompleteClient
										isExternalLoading={isLoading}
										labelSelect={"Cliente"}
										nameDefault={nomeClient}
										clientDefault={clientId}
										disabled={isReadOnly}
										onClientChange={(client) => {
											setSelectedClientTypeId(String(client?.clientType?.id ?? (client as any)?.clientTypeId ?? ""));
											// O perfil escolhido pode não valer para o novo cliente — limpa para forçar nova seleção.
											formRef.current?.setFieldValue("permissionId", "");
										}}
									/>
								</Grid>

								<Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }} />

								<Grid item xs={12} md={4}>
									<VTextField
										fullWidth
										label="Nome"
										name="name"
										disabled={isLoading || isReadOnly}
										onChange={(e) => setNome(e.target.value)}
									/>
								</Grid>

								<Grid item xs={12} md={4}>
									<VMaskTextField fullWidth label="Telefone" name="phone" disabled={isLoading || isReadOnly} mask="cell" />
								</Grid>
                                <Grid item xs={12} md={4}>
									<VTextField fullWidth label="Status" name="status" disabled={isLoading || isReadOnly} select>
										<MenuItem value={"active"}>Ativo</MenuItem>
										<MenuItem value={"inactive"}>Inativo</MenuItem>
										<MenuItem value={"blocked"}>Bloqueado</MenuItem>
									</VTextField>
								</Grid>

								<Grid item xs={12} md={isNew ? 4 : 6}>
									<VTextField
										fullWidth
										label="E-mail"
										name="email"
										disabled={isLoading || !isNew || isReadOnly}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Email />
												</InputAdornment>
											),
										}}
									/>
								</Grid>

								{isNew && (
									<Grid item xs={12} md={4}>
										<VTextField fullWidth label="Senha" name="password" disabled={isLoading || isReadOnly} type="password" />
									</Grid>
								)}

								<Grid item xs={12} md={isNew ? 4 : 6}>
									<AutoCompletePermission
										isExternalLoading={isLoading}
										labelSelect="Perfil"
										options={permissionOptions}
										disabled={isReadOnly}
									/>
								</Grid>
							</Grid>
						</Box>

						<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" padding={2}>
							<Typography variant="h6" color="primary" gutterBottom>
								Auditoria
							</Typography>

							{!user && (
								<Typography variant="body2" color="text.secondary">
									Após salvar, dados de auditoria aparecerão aqui.
								</Typography>
							)}

							{user && (
								<Stack spacing={1}>
									<Typography variant="subtitle2" fontWeight={"bold"} color={"text.secondary"}>
										Data do cadastro:{" "}
										{user?.createdAt || user?.created_at ? moment(user.createdAt || user.created_at).format("DD/MM/YYYY HH:mm") : "--"}
									</Typography>
									<Typography variant="subtitle2" fontWeight={"bold"} color={"text.secondary"}>
										Último login: {!user?.last_login ? "--" : moment(user.last_login).format("DD/MM/YYYY HH:mm")}
									</Typography>
								</Stack>
							)}
						</Box>
					</Stack>
				</Box>
			</VForm>
		</LayoutBaseDePagina>
	);
};
